import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import useWebMcp from "./hooks/useWebMcp";


const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const SubscriptionTerms = lazy(() => import("./pages/SubscriptionTerms"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const ProtectedAdminRoute = lazy(() => import("./components/ProtectedAdminRoute"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const Features = lazy(() => import("./pages/Features"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Security = lazy(() => import("./pages/Security"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogCategory = lazy(() => import("./pages/BlogCategory"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const DividendCalculator = lazy(() => import("./pages/DividendCalculator"));
const DividendenRechner = lazy(() => import("./pages/DividendenRechner"));
const RetirementCalculator = lazy(() => import("./pages/RetirementCalculator"));
const RentVsBuyCalculator = lazy(() => import("./pages/RentVsBuyCalculator"));
const CoastFireCalculator = lazy(() => import("./pages/CoastFireCalculator"));
const ToolsIndex = lazy(() => import("./pages/ToolsIndex"));
const HalalInvesting = lazy(() => import("./pages/HalalInvesting"));
const LpMaison = lazy(() => import("./pages/LpMaison"));

const RetireAt50 = lazy(() => import("./pages/retire-spoke/RetireAt50"));
const RetireAt55 = lazy(() => import("./pages/retire-spoke/RetireAt55"));
const RetireAt60 = lazy(() => import("./pages/retire-spoke/RetireAt60"));
const RetireAt67 = lazy(() => import("./pages/retire-spoke/RetireAt67"));
const JoinSession = lazy(() => import("./pages/JoinSession"));
const HalalDividendCalendarDeleteAccount = lazy(
  () => import("./pages/HalalDividendCalendarDeleteAccount"),
);
const HalalDividendCalendarPrivacy = lazy(
  () => import("./pages/HalalDividendCalendarPrivacy"),
);



const queryClient = new QueryClient();

const RTLHandler = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  useWebMcp();

  useEffect(() => {
    const dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return <>{children}</>;
};

const App = () => (

  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RTLHandler>
          <ScrollToTop />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/features" element={<Features />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/security" element={<Security />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/subscription-terms" element={<SubscriptionTerms />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/category/:slug" element={<BlogCategory />} />
              <Route path="/blog/how-much-to-retire-at-50" element={<RetireAt50 />} />
              <Route path="/blog/how-much-to-retire-at-55" element={<RetireAt55 />} />
              <Route path="/blog/how-much-to-retire-at-60" element={<RetireAt60 />} />
              <Route path="/blog/how-much-to-retire-at-67" element={<RetireAt67 />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/tools" element={<ToolsIndex />} />
              <Route path="/halal-investing" element={<HalalInvesting />} />
              <Route path="/tools/dividend-calculator" element={<DividendCalculator />} />

              <Route path="/tools/dividenden-rechner" element={<DividendenRechner />} />
              <Route path="/tools/retirement-calculator" element={<RetirementCalculator />} />
              <Route path="/tools/rent-vs-buy-calculator" element={<RentVsBuyCalculator />} />
              <Route path="/tools/coast-fire-calculator" element={<CoastFireCalculator />} />
              <Route path="/join" element={<JoinSession />} />
              {/* A/B landing page for ads and social links: noindex, not in the sitemap. */}
              <Route path="/lp/maison" element={<LpMaison />} />
              <Route
                path="/halal-dividend-calendar/delete-account"
                element={<HalalDividendCalendarDeleteAccount />}
              />
              <Route
                path="/halal-dividend-calendar/privacy"
                element={<HalalDividendCalendarPrivacy />}
              />


              <Route path="/auth" element={<Auth />} />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedAdminRoute>
                    <AdminAnalytics />
                  </ProtectedAdminRoute>
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
          </Suspense>
        </RTLHandler>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
